import getToken from './helpers/get-token';

/**
 * Form submission with AJAX.
 *
 * @example
 * <form action="/stage-ajax/postContact/">
 *
 * @class    AjaxForm
 * @param    {HTMLFormElement} form            - Form element.
 * @property {HTMLFormElement} form            - Form element.
 * @property {Object}          erroneousFields - Form fields with reported validity errors, keyed by name or ID.
 */

class AjaxForm {
  constructor(form) {
    this.form = form;
    this.erroneousFields = {};
    this.dataLayer = window.dataLayer || [];
    this.form.addEventListener('change', ({ target }) => this.clearErrors(target.name));
    this.form.addEventListener('submit', event => this.onSubmit(event));
  }

  /**
   * Clear form field of reported validity errors.
   *
   * @param  {string}     fieldName - Name of erroneous form field.
   *
   * @return {undefined}
   */

  clearErrors(fieldName) {
    if (fieldName in this.erroneousFields) {
      this.erroneousFields[fieldName].forEach(formField => formField.setCustomValidity(''));
      delete this.erroneousFields[fieldName];
    }
  }

  /**
   * Post form with AJAX on form submit and handle response.
   *
   * @param  {Event}     event - Form submit event.
   *
   * @return {undefined}
   */

  async onSubmit(event) {
    event.preventDefault();

    const {
      success,
      errors,
      redirectionUrl
    } = await this.postForm();

    if (success) {
      this.onSubmitSuccess(redirectionUrl);
    }
    else {
      const errorEntries = !Array.isArray(errors)
        ? this.getErrorEntries(errors)
        : errors;

      this.onSubmitFail(errorEntries);
    }
  }

  /**
   * Enrich form data with "stage-language" and "token", and post with AJAX.
   *
   * @return {Object} - Response to form submit.
   */

  async postForm() {
    const formData = new FormData(this.form);
    formData.set('stage-language', document.documentElement.lang || 'fi');

    const { token } = await getToken();
    formData.set('token', token);

    return await (
      await fetch(this.form.action, {
        method: 'POST',
        body: formData
      })
    ).json().catch(() => ({
      success: false,
      errors: [[
        this.form.querySelector('button[type="submit"]'),
        this.form.dataset.errorMessage || 'Lomakkeen lähetys epäonnistui.'
      ]]
    }));
  }

  /**
   * Dispatch success event, reset form, and redirect to success page.
   *
   * @param  {?string}   redirectionUrl - Success page URL.
   *
   * @return {undefined}
   */

  onSubmitSuccess(redirectionUrl) {
    this.form.dispatchEvent(new Event('submit-success'));
    this.form.reset();

    // Redirect to success page, if one is specified
    if (redirectionUrl) {
      location.href = redirectionUrl;
    }
  }

  /**
   * Dispatch fail event and report errors.
   *
   * @param  {Array}     errorEntries - Nested array containing pairs of form controls and corresponding error messages.
   *
   * @return {undefined}
   */

  onSubmitFail(errorEntries) {
    this.form.dispatchEvent(new Event('submit-fail'));
    this.dataLayer.push({formErrors: errorEntries.toString()});
    this.reportErrors(errorEntries);
  }

  /**
   * Get errors as entries, and format values.
   *
   * @param  {Object} errors - Object containing error messages keyed by corresponding field names.
   *
   * @return {Array}  - Nested array containing pairs of form controls and corresponding error messages.
   */

  getErrorEntries(errors) {
    return Object.entries(errors)
      .map(([fieldName, errorMessage]) => {
        // Get actual form control
        let formFields = this.form.elements[fieldName] || this.form.elements[`${fieldName}[]`];
        formFields = formFields instanceof NodeList ? Array.from(formFields) : [formFields];

        // Format error message
        const formattedErrorMessage = formFields[0] && `${this.getLabel(formFields[0])} ${errorMessage}.`;

        return [formFields, formattedErrorMessage];
      })
      .filter(([formField]) => formField); // Drop array elements of form controls that could not be found
  }

  /**
   * Report errors on invalid form fields.
   *
   * @param  {Array}     errorEntries - Nested array containing pairs of form controls and corresponding error messages.
   *
   * @return {undefined}
   */

  reportErrors(errorEntries) {
    errorEntries.forEach(([formFields, errorMessage]) => {
      this.erroneousFields[formFields[0].name] = formFields;

      formFields.forEach(formField => {
        formField.setCustomValidity(errorMessage);

        if (['input', 'textarea'].includes(formField.tagName.toLowerCase())) {
          // Reset error on input, if form field is input or textarea
          formField.addEventListener('input', ({ target }) => this.clearErrors(target.name), { once: true });
        }
      });
    });

    this.form.reportValidity();
  }

  /**
   * Get label text associated with given form field.
   *
   * @param  {HTMLElement} formField - Form control.
   *
   * @return {string}
   */

  getLabel(formField) {
    return formField.dataset.fieldLabel
      || formField.labels?.[0]?.textContent
        || document.querySelector(`label[for="${formField.id}"]`)?.textContent
          || formField.getAttribute('aria-label')
            || document.getElementById(formField.getAttribute('aria-labelledby')?.split(' ')[0])?.textContent
              || '';
  }
}

export default AjaxForm;
